import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

interface Genre {
  id: string;
  name: string;
}

export default function CreateSeries() {
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedGenreIds, setSelectedGenreIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [trailerUrl, setTrailerUrl] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  useEffect(() => {
    supabase.from('genres').select('id, name').order('name').then(({ data }: { data: Genre[] | null }) => {
      if (data) setGenres(data);
    });
  }, []);

  const toggleGenre = (id: string) => {
    setSelectedGenreIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publisher?.id) return;
    if (!title.trim()) { toast.error('Title is required'); return; }

    setSubmitting(true);
    try {
      let posterUrl: string | null = null;

      if (thumbnailFile) {
        const ext = thumbnailFile.name.split('.').pop();
        const path = `${publisher.id}/series/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('publisher-assets')
          .upload(path, thumbnailFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage
          .from('publisher-assets')
          .getPublicUrl(path);
        posterUrl = urlData.publicUrl;
      }

      // Insert work
      const { data: work, error: workError } = await supabase
        .from('works')
        .insert({
          publisher_id: publisher.id,
          title: title.trim(),
          description: description.trim() || null,
          type: 'tv_show',
          status: 'draft',
          poster_url: posterUrl,
          trailer_video_url: trailerUrl.trim() || null,
        })
        .select('id')
        .single();

      if (workError) throw workError;

      // Insert initial season 1
      await supabase.from('seasons').insert({
        work_id: work.id,
        season_number: 1,
      });

      // Insert genre associations
      if (selectedGenreIds.length > 0) {
        await supabase.from('work_genres').insert(
          selectedGenreIds.map(genre_id => ({ work_id: work.id, genre_id }))
        );
      }

      toast.success('Series created!');
      navigate(`/content/series/${work.id}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create series');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content/series')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Create TV Series</h1>
          <p className="text-muted-foreground mt-1">Set up a new show to add seasons and episodes</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="partner-card">
          <CardHeader>
            <CardTitle>Series Details</CardTitle>
            <CardDescription>Basic information about your TV series</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Series title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Synopsis or description of the series"
                rows={4}
                className="resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="thumbnail">Thumbnail / Poster</Label>
              <Input
                id="thumbnail"
                type="file"
                accept="image/*"
                onChange={e => setThumbnailFile(e.target.files?.[0] ?? null)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trailer">Trailer URL (optional)</Label>
              <Input
                id="trailer"
                value={trailerUrl}
                onChange={e => setTrailerUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </CardContent>
        </Card>

        <Card className="partner-card">
          <CardHeader>
            <CardTitle>Genres</CardTitle>
            <CardDescription>Select all genres that apply</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {genres.map(genre => (
                <Badge
                  key={genre.id}
                  variant={selectedGenreIds.includes(genre.id) ? 'default' : 'outline'}
                  className={`cursor-pointer transition-colors ${
                    selectedGenreIds.includes(genre.id)
                      ? 'bg-primary hover:bg-primary/80'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => toggleGenre(genre.id)}
                >
                  {genre.name}
                </Badge>
              ))}
            </div>
            {selectedGenreIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedGenreIds.length} genre{selectedGenreIds.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/content/series')}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Series'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
